// Modal compartido para conectar Tiendanube (estilo ATLAS). Se usa en 3 lugares:
//   • Mi marca (botón "Conectar") → con explicación (`showExplanation`).
//   • Import "Traer de Tiendanube" → SIN explicación (solo la nota multi-tienda).
//   • Historial de imágenes ("Exportar a Tiendanube" sin tienda conectada) → con
//     explicación PROPIA (`explanation`), porque ahí el objetivo es exportar, no importar.
// La nota multi-tienda + el link de logout aparecen SOLO si `companyHasStores`
// (la empresa ya tiene otra marca con Tiendanube conectada).

import React from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { TiendaNubeIcon } from "./TiendaNubeIcon";

export const TiendaNubeConnectModal: React.FC<{
  open: boolean;
  /** Muestra el párrafo "Conectá tu tienda para importar…". El import lo oculta. */
  showExplanation?: boolean;
  /** Reemplaza el texto por defecto de la explicación (requiere `showExplanation`). */
  explanation?: React.ReactNode;
  /** Si la empresa ya tiene otra tienda conectada → nota multi-tienda + link logout. */
  companyHasStores: boolean;
  connecting?: boolean;
  /** Mensaje de error del intento de conexión (ej. ventana emergente bloqueada).
   *  Se muestra arriba de los botones; sin esto los fallos eran invisibles
   *  (ADR-2026-08-07-tiendanube-popup-safari). */
  error?: string | null;
  onCancel: () => void;
  onConnect: () => void;
  /** Altura de apilado. Default 90 (por encima del AgregarVariosModal de Mi
   *  Marca, que va en 80). Cuando el modal que lo abre sube de altura —la
   *  Interfaz V2 lo hace para no quedar bajo sus paneles— hay que subir este
   *  también o queda tapado (ADR-684). Estilo inline: Tailwind no compila
   *  clases `z-[N]` armadas en runtime. */
  zIndex?: number;
}> = ({
  open,
  showExplanation = true,
  explanation,
  companyHasStores,
  connecting = false,
  error = null,
  onCancel,
  onConnect,
  zIndex = 90,
}) => {
  // No podemos saber el login real de Tiendanube (cross-origin). Aproximación:
  // una vez que tocan "Cerrar sesión en Tiendanube", ocultamos la nota (asumimos
  // que se deslogueó). Se resetea cada vez que se abre el modal.
  const [loggedOut, setLoggedOut] = React.useState(false);
  React.useEffect(() => {
    if (open) setLoggedOut(false);
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4"
      style={{ zIndex }}
    >
      <div className="bg-surface-card rounded-lg shadow-lg border border-neutral-200 max-w-sm w-full p-[var(--card-padding-mobile)] md:p-[var(--card-padding-desktop)] animate-modal-appear">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[length:var(--text-section-title-mobile)] font-semibold text-neutral-900">
            Conectar Tiendanube
          </h3>
          <button
            type="button"
            onClick={() => !connecting && onCancel()}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-6">
          <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
            <TiendaNubeIcon className="h-5 w-auto text-[#2c3357]" />
          </div>
          {showExplanation && (
            <p className="text-[length:var(--text-body)] text-neutral-600 text-center">
              {explanation ?? (
                <>
                  Conectá tu tienda de Tiendanube para{" "}
                  <span className="font-medium text-neutral-800">
                    importar tus productos
                  </span>{" "}
                  y usarlos en Atlas (generar imágenes, anuncios y más).
                </>
              )}
            </p>
          )}
          {/* Nota multi-tienda + logout: SOLO si la empresa ya tiene otra tienda
              Y no tocaron todavía "Cerrar sesión" (ahí asumimos que se deslogueó). */}
          {companyHasStores && !loggedOut && (
            <>
              <p
                className={`text-[12.5px] text-neutral-500 text-center ${
                  showExplanation ? "mt-3" : ""
                }`}
              >
                Ya tenés una tienda conectada en otra marca. Se conectará la que
                tengas abierta en Tiendanube ahora; para conectar otra, cerrá
                sesión en Tiendanube primero.
              </p>
              <div className="mt-2 text-center">
                <a
                  href="https://www.tiendanube.com/logout"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setLoggedOut(true)}
                  className="text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
                >
                  Cerrar sesión en Tiendanube →
                </a>
              </div>
            </>
          )}
          {error && (
            <p className="mt-3 text-[length:var(--text-caption)] text-error text-center">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={connecting}
            className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center bg-surface-card hover:bg-neutral-100 text-neutral-900 border border-neutral-200 rounded-md font-medium text-[length:var(--text-body)] shadow-sm transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConnect}
            disabled={connecting}
            className="h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-1.5 rounded-md shadow-sm font-medium text-[length:var(--text-body)] transition-colors bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
          >
            {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
            {connecting ? "Conectando…" : "Conectar"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TiendaNubeConnectModal;
